import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FONT, MEDIA_QUERY } from '../../styles/emotion';
import COLORS, { COLORS_BACKGROUND } from '../../styles/emotion/colors';

export type ButtonComponent = 'button' | 'a';

interface ButtonProps {
  variant?: string;
  compact?: boolean;
  disabled?: boolean;
  bordered?: boolean;
  fullWidth?: boolean;
  role?: string;
}

const primaryButtonCss = (props: ButtonProps) => {
  return css`
    background-color: ${COLORS_BACKGROUND.NAVY};
    color: ${COLORS.WHITE};
    :hover {
      color: ${COLORS.WHITE};
      background-color: ${COLORS.BLUE};
    }
    :active {
      background-color: ${COLORS_BACKGROUND.NAVY};
    }
    :disabled {
      background-color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
    }
    ${props.bordered &&
    `
    border-radius: 4px;
    border: solid 1px ${COLORS_BACKGROUND.WHITE};
  `}
  `;
};

const secondaryButtonCss = () => css`
  background-color: ${COLORS_BACKGROUND.WHITE};
  color: ${COLORS.BLUE};
  border-color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
  :hover {
    background-color: ${COLORS.GEYSER};
    color: ${COLORS.BLUE};
    border-color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
  }
  :active {
    background-color: ${COLORS_BACKGROUND.JEWEL_8};
    color: ${COLORS.BLUE};
  }
  :disabled {
    background-color: ${COLORS_BACKGROUND.GRAY_2};
    color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
  }
`;

const tertiaryButtonCss = () => css`
  background-color: ${COLORS.WHITE};
  color: ${COLORS.RED_MESSAGING};
  border-color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
  :hover {
    background-color: fade(${COLORS.RED_MESSAGING}, 10%);
    border-color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
  }
  :active {
    background-color: fade(${COLORS.RED_MESSAGING}, 30%);
  }
  :disabled {
    background-color: ${COLORS_BACKGROUND.GRAY_2};
    color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
  }
`;

const transparentButtonCss = () =>
  css`
    background-color: transparent;
    color: ${COLORS.WHITE};
    border-color: ${COLORS.WHITE};
    :disabled {
      background-color: ${COLORS_BACKGROUND.GRAY_2};
      color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
    }
  `;

const dangerButtonCss = () =>
  css`
    background-color: ${COLORS.RED_MESSAGING};
    color: ${COLORS.WHITE};
    :hover {
      color: ${COLORS.WHITE};
      background-color: ${COLORS.RED_MESSAGING};
    }
    :active {
      background-color: ${COLORS.RED_MESSAGING};
    }
    :disabled {
      background-color: ${COLORS_BACKGROUND.OUTLINE_GRAY};
    }
  `;

export const ButtonWrapper = styled.button`
  border: 1px solid transparent;
  text-align: center;
  cursor: pointer;
  font-size: ${FONT.FONT_SIZE_SMALL};
  font-weight: ${FONT.FONT_WEIGHT_SEMI_BOLD};
  border-radius: 4px;
  font-family: ${FONT.FONT_FAMILY_PROXIMA};
  height: 52px;
  padding-left: 16px;
  padding-right: 16px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 8px 0 ${COLORS.LIGHTER_SHADOW};
  ${MEDIA_QUERY.EXCLUDE_MOBILE} {
    height: 44px;
  }
  ${(props: ButtonProps) => props.compact && `height: 38px !important;`}
  ${(props: ButtonProps) => props.fullWidth && `width: 100%; flex-basis: 100%;`}
  ${(props: ButtonProps) => props.disabled && `cursor: default; box-shadow: none;`}
  :focus-visible {
    outline: 5px auto -webkit-focus-ring-color;
  }
  ${(props) =>
    props.as === 'a' &&
    `
  justify-content: center;
  align-items: center;
  user-select: none;
  text-align: center;
  &:active,
  &:focus,
  &:hover {
    text-decoration: none;
  }
  `}
  ${(props: ButtonProps) =>
    props.role === 'link' &&
    `
    box-shadow: none;
    background-color: transparent;
    border-radius: unset;
  `}
  ${(props: ButtonProps) => {
    switch (props.variant) {
      case 'primary':
        return primaryButtonCss(props);
      case 'secondary':
        return secondaryButtonCss();
      case 'tertiary':
        return tertiaryButtonCss();
      case 'transparent':
        return transparentButtonCss();
      case 'danger':
        return dangerButtonCss();
      default:
        return primaryButtonCss(props);
    }
  }}
`;
